import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useCallback, useEffect, useRef } from 'react'
import { Link } from 'react-router'
import { useMount } from 'react-use'

import ExternalLink from '@/components/misc/ExternalLink'
import { useLoadPendingPaymentsMutation } from '@/components/misc/Payments/hooks/useLoadPendingPaymentsMutation'
import { useRefreshCurrentPaymentsMutation } from '@/components/misc/Payments/hooks/useRefreshCurrentPaymentsMutation'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldLabel } from '@/components/ui/field'
import { Spinner } from '@/components/ui/spinner'
import useAppDispatch from '@/hooks/useAppDispatch'
import useAppSelector from '@/hooks/useAppSelector'
import {
  PAYMENTS_PROCESSING_REFRESH_INTERVAL,
  PAYMENTS_RELOAD_REFRESH_INTERVAL,
} from '@/lib/constants'
import { faCheck, faCheckCircle } from '@/lib/fontAwesomePro/solid'
import { getApiUrl } from '@/lib/helpers/apiHelpers'
import { cn } from '@/lib/utils'
import {
  clearRefreshTimeout,
  dismissPayment,
  selectPayments,
  setIsOutdated,
  setIsProcessing,
  setNextBanner,
  setNextBlock,
  setPrevBanner,
  setPrevBlock,
  setRefreshDelay,
  setRefreshTimeout,
} from '@/redux/slices/payments'

const Payments: React.FC = () => {
  const paymentsState = useAppSelector(selectPayments)
  const loadPendingPaymentMutation = useLoadPendingPaymentsMutation()
  const refreshCurrentPaymentMutation = useRefreshCurrentPaymentsMutation()
  const dispatch = useAppDispatch()
  // Keep track of the outstanding payments reported in the header (useRef needed for the event handler)
  const outstandingPayments = useRef(0)

  const loadPendingPayments = useCallback(() => {
    // Load all pending payments
    // If there are any pending refresh (set paymentsState.isOutdated) in PAYMENTS_RELOAD_REFRESH_INTERVAL
    loadPendingPaymentMutation.mutate(undefined, {
      onSuccess: (data) => {
        const items = data['hydra:member']

        if (
          items.length > 0 &&
          items.some((payment) => payment.status === 'PENDING')
        ) {
          dispatch(setRefreshDelay(PAYMENTS_RELOAD_REFRESH_INTERVAL))
          dispatch(
            setRefreshTimeout(
              setTimeout(() => {
                dispatch(setIsOutdated(true))
              }, PAYMENTS_RELOAD_REFRESH_INTERVAL),
            ),
          )
        }
      },
    })
  }, [loadPendingPaymentMutation, dispatch])

  const onOutstandingPayments = React.useCallback(
    (event: Event) => {
      // Handle the outstanding payments header event
      if (event instanceof CustomEvent) {
        const serverOutstandingPayments = Number(event.detail)
        if (serverOutstandingPayments > outstandingPayments.current) {
          // If the outstanding payments have increased, reload the pending payments
          loadPendingPayments()
        }
        outstandingPayments.current = serverOutstandingPayments
      }
    },
    [loadPendingPayments, outstandingPayments],
  )

  useMount(() => {
    loadPendingPayments()

    // Register event listener to keep track of outstanding payments in the header
    window.addEventListener('outstandingPayments', onOutstandingPayments)

    return () => {
      // Unregister event listener
      window.removeEventListener('outstandingPayments', onOutstandingPayments)
    }
  })

  useEffect(() => {
    // Keep track of paymentsState.isOutdated state
    // If true then refresh all currently loaded payments
    // If any is still pending, refresh (set paymentsState.isOutdated) again in predefined time
    if (paymentsState.isOutdated) {
      dispatch(setIsOutdated(false))
      refreshCurrentPaymentMutation.mutate(paymentsState, {
        onSuccess: (data) => {
          const items = data['hydra:member']
          if (
            items.length > 0 &&
            items.some((payment) => payment.status === 'PENDING')
          ) {
            dispatch(
              setRefreshTimeout(
                setTimeout(() => {
                  dispatch(setIsOutdated(true))
                }, paymentsState.refreshDelay),
              ),
            )
          } else {
            dispatch(clearRefreshTimeout())
          }
        },
      })
    }
  }, [
    paymentsState.isOutdated,
    paymentsState.refreshDelay,
    dispatch,
    paymentsState,
    refreshCurrentPaymentMutation,
  ])

  function startProcessing(paymentId: number) {
    // Start processing payment, set refresh interval and trigger outdated
    dispatch(setIsProcessing({ paymentId, isProcessing: true }))
    dispatch(setRefreshDelay(PAYMENTS_PROCESSING_REFRESH_INTERVAL))
    dispatch(
      setRefreshTimeout(
        setTimeout(() => {
          dispatch(setIsOutdated(true))
        }, PAYMENTS_PROCESSING_REFRESH_INTERVAL),
      ),
    )
  }

  return (
    <>
      <PaymentsBanner startProcessing={startProcessing} />
      <PaymentsBlock startProcessing={startProcessing} />
    </>
  )
}

function PaymentsBanner({
  startProcessing,
}: {
  startProcessing: (paymentId: number) => void
}) {
  const paymentsState = useAppSelector(selectPayments)
  const dispatch = useAppDispatch()

  if (paymentsState.banner.total > 0 && paymentsState.banner.currentId) {
    const currentPayment = paymentsState.payments.find(
      (payment) => payment.id === paymentsState.banner.currentId,
    )
    const currentPage = paymentsState.banner.ids.indexOf(
      paymentsState.banner.currentId,
    )

    if (currentPayment) {
      let ctaText, bannerMessage
      if (currentPayment.type == 'DIRECT_DEBIT_MANDATE') {
        bannerMessage =
          'To keep enjoying RQ, you’ll need to set up your subscription'
        ctaText = 'Set up'
      } else {
        bannerMessage = 'To keep enjoying RQ, you’ll need to make payment'
        ctaText =
          'Pay ' +
          currentPayment.total.toLocaleString('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0,
          })
      }

      if (currentPayment.description) {
        bannerMessage = currentPayment.description
      }

      if (currentPayment.status == 'CONFIRMED') {
        bannerMessage =
          'Thank you, ' +
          (currentPayment.type == 'DIRECT_DEBIT_MANDATE'
            ? 'your subscription has been confirmed'
            : 'your payment has been received')
      }

      return (
        <>
          <section
            className={cn(
              `text-md flex items-center justify-between gap-4 p-4 text-center
              text-white`,
              currentPayment.status == 'CONFIRMED'
                ? 'bg-success'
                : 'bg-warning',
            )}
          >
            <div
              className="mr-auto flex w-[130px] items-center justify-between
                gap-1"
            >
              {paymentsState.banner.total > 1 && (
                <>
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      dispatch(setPrevBanner())
                    }}
                  >
                    ⟨
                  </Button>
                  <span>
                    {currentPage + 1} of {paymentsState.banner.total}
                  </span>
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      dispatch(setNextBanner())
                    }}
                  >
                    ⟩
                  </Button>
                </>
              )}
            </div>

            <span className="font-medium">{bannerMessage}</span>

            <div className="ml-auto">
              <Link
                className="mr-2"
                to="https://meetings-eu1.hubspot.com/johnny-ridd/rq-onboarding"
                target="_blank"
              >
                <Button variant="outline-secondary">Talk to Sales</Button>
              </Link>

              {currentPayment.status == 'PENDING' && (
                <Button
                  asChild
                  variant="outline-secondary"
                  className="ml-auto font-semibold"
                  onClick={() => {
                    startProcessing(currentPayment.id)
                  }}
                >
                  <a
                    href={getApiUrl() + currentPayment.publicLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {ctaText}
                  </a>
                </Button>
              )}

              {currentPayment.status == 'CONFIRMED' && (
                <Button
                  variant="outline-secondary"
                  className="ml-auto font-semibold"
                  onClick={() => {
                    dispatch(dismissPayment(currentPayment.id))
                  }}
                >
                  Close
                </Button>
              )}
            </div>
          </section>
        </>
      )
    }
  }

  return null
}

function PaymentsBlock({
  startProcessing,
}: {
  startProcessing: (paymentId: number) => void
}) {
  const paymentsState = useAppSelector(selectPayments)
  const dispatch = useAppDispatch()
  const [checkbox1Checked, setCheckbox1Checked] = React.useState(false)
  const [checkbox2Checked, setCheckbox2Checked] = React.useState(false)

  if (paymentsState.block.total > 0 && paymentsState.block.currentId) {
    const currentPayment = paymentsState.payments.find(
      (payment) => payment.id === paymentsState.block.currentId,
    )

    const currentPage = paymentsState.block.ids.indexOf(
      paymentsState.block.currentId,
    )

    if (currentPayment) {
      const totalCurrency = currentPayment.total.toLocaleString('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 2,
      })

      const netCurrency = currentPayment.net.toLocaleString('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 2,
      })

      return (
        <Dialog
          open
          onHide={() => {
            dispatch(dismissPayment(currentPayment.id))
          }}
        >
          <DialogContent
            size="lg"
            showCloseButton={currentPayment.status === 'CONFIRMED'}
          >
            <DialogHeader className="border-b-0">
              <DialogTitle>
                Secure your access to the UK’s best referral platform
                <span className="text-body-base text-dim block">
                  To keep enjoying RQ, you’ll need to set up your subscription.
                </span>
              </DialogTitle>
            </DialogHeader>

            <DialogBody>
              <div className="flex items-center rounded-md bg-[##f3faff] py-6">
                <div className="flex w-1/2 items-center justify-center">
                  <div className="text-body-base text-dim font-light">
                    <span
                      className=""
                      style={{
                        fontSize: '4em',
                        color: 'black',
                        lineHeight: '1.1em',
                      }}
                    >
                      {netCurrency}
                    </span>
                    {currentPayment.type == 'DIRECT_DEBIT_MANDATE' &&
                      ' / month'}
                    <br />
                    <span className="italic">+VAT</span>
                  </div>
                </div>

                <ul className="text-md mb-4 w-1/2">
                  <ListItem>Integrated referral management</ListItem>
                  <ListItem>Efficient professional collaboration</ListItem>
                  <ListItem>Secure data sharing and automation</ListItem>
                </ul>
              </div>
            </DialogBody>

            <DialogFooter>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-full lg:col-span-7">
                  {currentPayment.type == 'DIRECT_DEBIT_MANDATE' ? (
                    <>
                      <Field
                        orientation="horizontal"
                        className="mb-2 flex items-start"
                      >
                        <Checkbox
                          className="text-dim mt-1"
                          name="checkbox1"
                          id="checkbox1"
                          disabled={currentPayment.isProcessing}
                          checked={checkbox1Checked}
                          onCheckedChange={(isChecked) =>
                            setCheckbox1Checked(!!isChecked)
                          }
                        />

                        <FieldLabel htmlFor="checkbox1" className="text-dim">
                          {`I agree to RQ taking a payment of ${totalCurrency} on the first day of each month. `}
                        </FieldLabel>
                      </Field>

                      <Field
                        orientation="horizontal"
                        className="flex items-start"
                      >
                        <Checkbox
                          className="text-dim mt-1"
                          id="checkbox2"
                          disabled={currentPayment.isProcessing}
                          checked={checkbox2Checked}
                          onCheckedChange={(isChecked) =>
                            setCheckbox2Checked(!!isChecked)
                          }
                        />

                        <FieldLabel htmlFor="checkbox2" className="text-dim">
                          <div>
                            I understand that this price is subject to change
                            based upon my usage. Any changes to pricing will be
                            clearly communicated prior to any billing period.
                            You can see details on how RQ’s pricing works here
                            at{' '}
                            <ExternalLink href="https://rq.app/about/pricing">
                              rq.app/about/pricing
                            </ExternalLink>
                          </div>
                        </FieldLabel>
                      </Field>
                    </>
                  ) : (
                    <>
                      To learn more about how our pricing works, please visit:{' '}
                      <a
                        href="https://rq.app/about/pricing"
                        target="_blank"
                        rel="noreferrer"
                      >
                        rq.app/about/pricing
                      </a>
                    </>
                  )}
                </div>

                <div
                  className="col-span-full mt-6 text-right lg:col-span-5
                    lg:mt-0"
                >
                  <Link
                    to="https://meetings-eu1.hubspot.com/johnny-ridd/rq-onboarding"
                    target="_blank"
                  >
                    <Button variant="outline-secondary">Talk to Sales</Button>
                  </Link>

                  {currentPayment.status == 'PENDING' && (
                    <Button
                      variant="primary"
                      className="ml-2 px-6"
                      disabled={
                        (!checkbox1Checked || !checkbox2Checked) &&
                        currentPayment.type == 'DIRECT_DEBIT_MANDATE'
                      }
                      onClick={() => {
                        startProcessing(currentPayment.id)
                      }}
                      asChild
                    >
                      <a
                        href={getApiUrl() + currentPayment.publicLink}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2"
                      >
                        {currentPayment.isProcessing ? (
                          <>
                            <span>Processing</span>
                            <Spinner />
                          </>
                        ) : (
                          <>
                            {currentPayment.type == 'DIRECT_DEBIT_MANDATE' &&
                              'Set up direct debit'}

                            {currentPayment.type == 'ONE_OFF_PAYMENT' &&
                              `Pay ${totalCurrency}`}
                          </>
                        )}
                      </a>
                    </Button>
                  )}

                  {currentPayment.status == 'CONFIRMED' && (
                    <Button
                      variant="success"
                      className="px-6"
                      onClick={() => {
                        dispatch(dismissPayment(currentPayment.id))
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faCheck}
                        style={{ marginRight: '6px' }}
                      />
                      Paid
                    </Button>
                  )}

                  <div
                    className="mt-4 ml-auto flex w-[130px] items-center
                      justify-between gap-1"
                  >
                    {/* Pagination */}
                    {paymentsState.block.total > 1 && (
                      <>
                        <Button
                          variant="outline-primary"
                          onClick={() => {
                            dispatch(setPrevBlock())
                          }}
                        >
                          ⟨
                        </Button>
                        <span>
                          {currentPage + 1} of {paymentsState.block.total}
                        </span>
                        <Button
                          variant="outline-primary"
                          onClick={() => {
                            dispatch(setNextBlock())
                          }}
                        >
                          ⟩
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )
    }
  }

  return null
}

function ListItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="my-1 flex items-center">
      <FontAwesomeIcon
        icon={faCheckCircle}
        className="text-success mr-2"
        size="xl"
      />
      {children}
    </li>
  )
}

export default Payments
